import React, { useEffect, useMemo, useState } from 'react';
import { Download, Share, X } from 'lucide-react';

const DISMISS_KEY = 'buscadoutor_pwa_install_dismissed_at';
const DISMISS_DAYS = 14;

function isStandaloneDisplay() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isDismissedRecently() {
  const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
  if (!dismissedAt) return false;
  const ageMs = Date.now() - dismissedAt;
  return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

function isIosSafari() {
  const ua = window.navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

function isAndroid() {
  return /Android/i.test(window.navigator.userAgent || '');
}

export default function PwaInstallPrompt() {
  const [installEvent, setInstallEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  const isMobileLike = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 640px)').matches;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    setIsInstalled(isStandaloneDisplay());
    if (isStandaloneDisplay() || isDismissedRecently()) return undefined;

    if (isIosSafari()) {
      const timer = window.setTimeout(() => {
        setShowIosHint(true);
        setVisible(true);
      }, 1600);
      return () => window.clearTimeout(timer);
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
      setVisible(true);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setVisible(false);
      setInstallEvent(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
    setVisible(false);
  };

  if (!visible || isInstalled || !isMobileLike || (!installEvent && !showIosHint)) return null;

  const Icon = showIosHint ? Share : Download;
  const helperText = showIosHint
    ? 'No iPhone, toque em compartilhar e escolha Adicionar a Tela de Inicio. No Android, use o menu do navegador e toque em Instalar app ou Adicionar a tela inicial.'
    : isAndroid()
      ? 'No Android, toque em Instalar para adicionar o BuscaDoutor a tela inicial.'
      : 'Toque em Instalar para adicionar o BuscaDoutor a tela inicial do celular.';

  return (
    <div className="fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-[80] sm:left-auto sm:right-5 sm:w-[380px]">
      <div className="rounded-2xl border border-primary/15 bg-white p-4 shadow-[0_18px_50px_rgba(28,78,66,0.18)]">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-900">Instale o BuscaDoutor</p>
            <p className="mt-1 text-sm leading-5 text-slate-600">
              {helperText}
            </p>
            <div className="mt-3 flex gap-2">
              {!showIosHint && (
                <button
                  type="button"
                  onClick={install}
                  className="min-h-10 rounded-full bg-primary px-4 text-sm font-semibold text-white"
                >
                  Instalar
                </button>
              )}
              <button
                type="button"
                onClick={dismiss}
                className="min-h-10 rounded-full border border-slate-200 px-4 text-sm font-semibold text-slate-600"
              >
                Agora n?o
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Fechar"
            className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
